/**
 * @import { HTMLElementInfo } from 'spart/js/core.js'
 */
import store from 'util/store';
import openPage from 'spart/js/page.js';
import { getPageHeader, getOptionsButton } from 'spart/js/component.js';
import { copyText, shareText } from 'spart/js/navigator.js';
import { toast, updateToast, removeToast, newBusyToast } from 'spart/js/toast.js';
import {
	createElement,
	updateElement,
	createSVGElement,
} from 'spart/js/core.js';
import { _fetch, sendData, showProblemDetail } from 'spart/js/fetch.js';
import { tl } from 'spart/js/i18n.js';

const optionsButtonSvgElem = createSVGElement(window["svgStrings"].optionsButton);

function isDeletedMessage(message) {
	// allow empty string content but not null which indicates deletion
	return !message || message.content == null;
}

/**
 * @param {MouseEvent} e
 */
function onReplySnippet(e) {
	const target = /** @type {HTMLButtonElement} */ (e.target);
	const elem = document.getElementById(target.dataset.id);
	if (elem) elem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

/**
 * @param {string} name
 */
function isAI(name) {
	return name == 'AI' || name == 'IA';
}

function getMessageTimeSent(message) {
	const date = new Date(message.dateSent);
	const hh = String(date.getHours()).padStart(2, '0');
	const mm = String(date.getMinutes()).padStart(2, '0');
	return `${hh}:${mm}`;
}

class PageInfo {
	/**
	 * @param {HTMLDivElement} page
	 * @param {string} search
	 * @param {number} roomId
	 */
	constructor(page, search, roomId) {
		this.messagesMap = {}; // messages stored by id for re-use
		this.search = search;
		this.room = { id: roomId };

		// DOM elements
		this.page = page;
		this.chatContainer = null;
		this.pageFooter = null;
		this.readAloudElem = null;
		this.messageInput = null;
		this.titleElem = null;

		// parent message to reply to
		this.replyText = null;
		this.replyPreview = null;

		// Fetch messages from the API
		this.fetching = false;
		this.isonline = true; // Assume online initially
		this.fetchTimerId = null;

		this.lastMessageDateSent = '';
		this.latestMsgDate = '';
	}

	// Auto-scroll the chat container to the bottom
	scrollToBottom() {
		this.chatContainer.scrollTop = this.chatContainer.scrollHeight;
	}

	// Send a new message to the API
	sendMessage() {
		const content = this.messageInput.value.trim();
		if (!content) return; // Do nothing if the message is empty

		// Build the message payload
		const payload = {
			roomId: this.room.id,
			parentId: this.replyText.dataset.id || null,
			content: content,
		};
		sendData('/api/message/send', 'POST', payload).then(async (response) => {
			if (response.ok) {
				// Clear input and reset reply state
				this.messageInput.value = '';
				this.cancelReply();
				this.shouldScroll = true;

				// Fetch the new message immediately
				this.fetchMessages();

				const info = await response.json();
				this.uiForBusyAI(info.ai_is_busy);

			} else showProblemDetail(response);
		});
	}

	/**
	 * @param {MouseEvent} e
	 */
	async joinGroup(e) {
		const target = /** @type {HTMLButtonElement} */ (e.target);
		target.disabled = true;
		const url = '/api/room/join?' + this.search;
		const response = await _fetch(url, { method: 'POST' });
		if (response.ok) {
			this.room.joined = true;
			this.setPageFooter();
		} else showProblemDetail(response);
		target.disabled = false;
	}

	playAudio(data) {
		if (!this.page.isConnected) {
			console.warn(
				`Skipped playAudio() as page ${this.page.id} is not connected.`,
			);
			return;
		}
		const audio = this.readAloudElem.querySelector('audio');
		if (audio.src)
			// if one already loaded
			this.cancelAudio();
		this.readAloudElem.hidden = false;
		audio.src = data.url;
		audio.play(); // start playback
		audio.focus(); // only works if element is visible and not disabled
	}

	cancelAudio() {
		const elem = this.readAloudElem;
		if (elem) {
			const audio = elem.querySelector('audio');
			if (audio.src) {
				audio.pause();
				URL.revokeObjectURL(audio.src);
				audio.removeAttribute('src');
				audio.load(); // reset the element
			}
			elem.hidden = true;
		}
	}

	// Cancel the current reply
	cancelReply() {
		// Do not use null below as it is converted to a string
		this.replyText.dataset.id = '';
		this.replyText.textContent = '';
		this.replyPreview.hidden = true;
	}

	setPageFooter() {
		/** @type {HTMLElementInfo[]} */
		let content = [
			{
				tag: 'div',
				class: 'audio-playback',
				hidden: true,
				callback: (elem) => (this.readAloudElem = elem),
				content: [
					{
						tag: 'audio',
						attri: { controls: true },
					},
					{
						tag: 'button',
						class: 'cancel',
						html: 'x',
						events: { click: this.cancelAudio.bind(this) },
					},
				],
			},
			{
				tag: 'div',
				class: 'reply-preview',
				hidden: true,
				callback: (elem) => (this.replyPreview = elem),
				content: [
					{
						tag: 'span',
						class: 'reply-text',
						events: { click: onReplySnippet },
						callback: (elem) => (this.replyText = elem),
					},
					{
						tag: 'button',
						class: 'cancel',
						html: 'x',
						events: { click: this.cancelReply.bind(this) },
					},
				],
			},
			{
				tag: 'div',
				class: 'input-area',
				content: [
					{
						tag: 'textarea',
						class: 'message-input',
						placeholder: 'Type a message',
						attri: { rows: '3' },
						callback: (elem) => {
							if (elem instanceof HTMLTextAreaElement)
								this.messageInput = elem;
						},
					},
					{
						tag: 'button',
						title: 'Send',
						id: 'send-message',
						class: 'bi bi-send-fill send-btn',
						events: { click: this.sendMessage.bind(this) },
					},
				],
			},
		];

		if (!this.room.joined) {
			content = [
				{
					tag: 'div',
					class: 'join-this-group',
					content: [
						{
							tag: 'button',
							class: 'btn btn-success',
							text: 'Join this group',
							events: { click: this.joinGroup.bind(this) },
						},
					],
				},
			];
		}
		updateElement(this.pageFooter, { content });
	}

	async fetchMessages() {
		if (this.fetching) return;
		this.fetching = true;

		let url = '/api/room/messages?' + this.search;
		url += '&lastMessageDateSent=' + this.lastMessageDateSent;

		const response = await _fetch(url);

		if (!response.status) {
			if (this.isonline) {
				this.isonline = false;
				showProblemDetail(response);
			}
			this.fetching = false;
			return;
		}
		this.isonline = true;

		if (!response.ok) {
			showProblemDetail(response);
			this.fetching = false;
			return;
		}

		// process the successful response
		const content = await response.json();

		store.putMessages(content);
		this.setMessages(content);

		this.fetching = false;
	}

	setMessages(content) {
		const room = content.roomInfo;

		const firstTime = !this.room.name;
		if (firstTime) {
			this.room = room;
			this.titleElem.textContent = room.name;
			this.setPageFooter();
		}

		this.uiForBusyAI(room.state == 2);

		if (content.messages.length > 0) {
			content.messages.forEach((message) => {
				// Store message
				this.messagesMap[message.id] = message;
				this.appendMessage(message);

				if (this.lastMessageDateSent < message.dateSent) {
					this.lastMessageDateSent = message.dateSent;
				}
			});

			if (firstTime || this.shouldScroll) {
				this.shouldScroll = false;
				this.scrollToBottom();
			}
		}

		// below comes after as messages must be added to the DOM first
		this.changeSkippedMessage(room.skippedMessageId, firstTime);
	}

	/**
	 * @param {boolean} aiBusy
	 */
	uiForBusyAI(aiBusy) {
		if (aiBusy)
			this.aiBusyToast = updateToast(this.aiBusyToast, 'AI is busy responding, please wait', 60);
		else
			removeToast(this.aiBusyToast);

		const sendBtn = document.getElementById('send-message');
		if (sendBtn) sendBtn.hidden = aiBusy;
	}

	shareLink() {
		const r = this.room;
		let url = window.location.origin;
		url += `/chat?r=${r.id}&k=${r.joinKey}`;
		shareText(r.name, undefined, url);
	}

	onPushMessage(event) {
		const data = event.data;
		console.log("Message received", data);
		if (data && data.roomId === this.room.id) {
			this.fetchMessages();
		}
	}

	initPage() {
		if ('serviceWorker' in navigator) {
			navigator.serviceWorker.addEventListener('message', this.onPushMessage.bind(this));
		}

		this.page.addEventListener('page-closed', () => {
			if ('serviceWorker' in navigator) {
				navigator.serviceWorker.removeEventListener('message', this.onPushMessage.bind(this));
			}
			clearInterval(this.fetchTimerId);
			this.cancelAudio();
		});

		const menuOptions = [
			{
				text: 'Share the link to this room',
				events: { click: this.shareLink.bind(this) },
			},
		];

		const titleElem = {
			tag: 'span',
			callback: (elem) => (this.titleElem = elem),
		};
		const header = getPageHeader(true, titleElem, undefined, menuOptions);

		const content = [
			header,
			{
				tag: 'div',
				class: 'page-content',
				callback: (elem) => (this.chatContainer = elem),
			},
			{
				tag: 'div',
				class: 'page-footer',
				callback: (elem) => (this.pageFooter = elem),
			},
		];
		updateElement(this.page, { content });

		const indicator = document.getElementById('app-loading-indicator');
		const wasHidden = indicator.hidden;
		if (wasHidden) indicator.hidden = false;

		return store
			.getMessages(this.room.id)
			.then((content) => {
				if (content) this.setMessages(content);
				else return this.fetchMessages();
			})
			.then(() => {
				this.fetchTimerId = setInterval(this.fetchMessages.bind(this), 4000);
				if (wasHidden) indicator.hidden = true;
			});
	}

	getMessageFromEvent(event) {
		const elem = event.target.closest('.chat-message');
		return this.messagesMap[elem.id];
	}

	onCopyMessage(e) {
		const message = this.getMessageFromEvent(e);
		copyText(message.content);
	}

	setReplySnippet(elem, messageId) {
		const message = this.messagesMap[messageId];
		if (isDeletedMessage(message)) {
			updateElement(elem, { text: 'Reply to a deleted message' });
			return;
		}

		const maxLength = 127;
		let msg = message.content;
		if (msg.length > maxLength) msg = msg.slice(0, maxLength) + '...';

		const sender = message.senderName;
		const tag = isAI(sender) ? 'span' : null;
		const content = [{ text: sender, tag }, { text: ': ' + msg }];
		updateElement(elem, { content });
		elem.dataset.id = messageId;
	}

	onReplyButton(e) {
		if (!this.room.joined) {
			toast('Join this group');
			return;
		}
		const message = this.getMessageFromEvent(e);

		this.setReplySnippet(this.replyText, message.id);
		this.replyPreview.hidden = false;

		const mi = this.messageInput;
		if (!mi.value) {
			const sender = message.senderName;
			if (isAI(sender)) mi.value = '@' + tl(sender) + ' ';
		}
		mi.focus();
	}

	onReadAloud(e) {
		const busy = newBusyToast();
		const message = this.getMessageFromEvent(e);
		const url = '/api/message/read-aloud?id=' + message.id;
		_fetch(url).then((response) => {
			removeToast(busy);
			if (response.ok) return response.json().then(this.playAudio.bind(this));
			else showProblemDetail(response);
		});
	}

	onDeleteMessage(e) {
		const message = this.getMessageFromEvent(e);
		if (window.confirm(tl('Please confirm you want to delete'))) {
			const url = '/api/message/delete?id=' + message.id;
			_fetch(url, { method: 'DELETE' }).then((response) => {
				if (response.ok) {
					const elem = document.getElementById(message.id);
					elem.remove();
					message.content = null;
				} else showProblemDetail(response);
			});
		}
	}

	changeSkippedMessage(messageId, firstTime) {
		let id = this.room.skippedMessageId;
		if (id == messageId && !firstTime) return;

		if (id) {
			const elem = document.getElementById(id);
			if (elem) elem.classList.remove('ai-skipped');
		}

		id = messageId;
		this.room.skippedMessageId = id;

		if (id) {
			const elem = document.getElementById(id);
			if (elem) elem.classList.add('ai-skipped');
		}
	}

	onHideFromAI(e) {
		const message = this.getMessageFromEvent(e);

		if (!localStorage.firstHideFromAI) {
			localStorage.firstHideFromAI = Date.now();
			window.alert(tl('All prior messages will be skipped'));
		}

		const url = '/api/message/hide-from-ai?id=' + message.id;
		_fetch(url, { method: 'PATCH' }).then((response) => {
			if (response.ok) this.changeSkippedMessage(message.id);
			else showProblemDetail(response);
		});
	}

	appendDateSeparator(message) {
		const dateStr = new Date(message.dateSent).toDateString();
		if (this.latestMsgDate == dateStr) return;
		this.latestMsgDate = dateStr;
		const info = {
			tag: 'p',
			class: 'date-separator',
			content: [{ tag: 'span', content: [{ text: this.latestMsgDate }] }],
		};
		this.chatContainer.appendChild(createElement(info));
	}

	sentOrCausedByMe(message) {
		while (true) {
			if (!message) break;

			if (message.sentByMe) return true;

			if (!isAI(message.senderName) || !message.parentId) break;

			message = this.messagesMap[message.parentId];
		}
		return false;
	}

	createMessageOptionsButton(message) {
		const options = [
			{ text: 'Copy', events: { click: this.onCopyMessage.bind(this) } },
			{ text: 'Reply', events: { click: this.onReplyButton.bind(this) } },
			{ text: 'Read Aloud', events: { click: this.onReadAloud.bind(this) } },
		];

		if (this.sentOrCausedByMe(message)) {
			options.push({
				text: 'Delete',
				class: 'delete-msg',
				events: { click: this.onDeleteMessage.bind(this) },
			});
			options.push({
				text: 'Hide from AI',
				class: 'hide-from-ai',
				events: { click: this.onHideFromAI.bind(this) },
			});
		}

		const icon = optionsButtonSvgElem.cloneNode(true);
		const info = { element: /** @type {HTMLElement} */ (icon) };
		return getOptionsButton(info, options);
	}

	// Append a message to the chat container
	appendMessage(message) {
		if (isDeletedMessage(message)) return;

		this.appendDateSeparator(message);

		const content = [
			this.createMessageOptionsButton(message),
			{ tag: 'div', class: 'sender-name', text: message.senderName },
			message.parentId && {
				tag: 'div',
				class: 'reply-snippet',
				events: { click: onReplySnippet },
				callback: (elem) => this.setReplySnippet(elem, message.parentId),
			},
			{
				tag: 'div',
				class: 'content',
				callback: (elem) => window["convertMarkdownText"](elem, message.content, true),
			},
			{
				tag: 'div',
				class: 'chat-message-footer',
				content: [
					{
						tag: 'button',
						class: 'reply-btn',
						text: 'Reply',
						events: { click: this.onReplyButton.bind(this) },
					},
					{
						tag: 'span',
						class: 'date-sent',
						content: [{ text: getMessageTimeSent(message) }],
					},
				],
			},
		];

		const className =
			'chat-message ' + (message.sentByMe ? 'sent' : 'received');

		this.chatContainer.appendChild(
			createElement({
				tag: 'div',
				id: message.id,
				class: className,
				content,
			}),
		);
	}
}

/**
 * Open the Chat page
 * @param {URLSearchParams} params
 * @returns {Promise<void>}
 */
export default function openChatPage(params) {
	const page = openPage('chat');
	page.classList.add('flex-column');

	if (page.childElementCount) {
		console.warn('How did we get here?');
		return;
	}

	const search = params.toString();
	const roomId = Number(params.get('r'));

	const x = new PageInfo(page, search, roomId);
	return x.initPage();
}
